/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CanonicalToolAttributes,
  CanonicalToolAttributes$inboundSchema,
} from "./canonicaltoolattributes.js";
import {
  ToolAnnotations,
  ToolAnnotations$inboundSchema,
} from "./toolannotations.js";
import { ToolVariation, ToolVariation$inboundSchema } from "./toolvariation.js";

/**
 * The transport type used to connect to the MCP server
 */
export const TransportType = {
  StreamableHttp: "streamable-http",
  Sse: "sse",
} as const;
/**
 * The transport type used to connect to the MCP server
 */
export type TransportType = ClosedEnum<typeof TransportType>;

/**
 * A proxy tool that references an external MCP server
 */
export type ExternalMCPToolDefinition = {
  /**
   * Tool annotations providing behavioral hints about the tool
   */
  annotations?: ToolAnnotations | undefined;
  /**
   * The original details of a tool
   */
  canonical?: CanonicalToolAttributes | undefined;
  /**
   * The canonical name of the tool. Will be the same as the name if there is no variation.
   */
  canonicalName: string;
  /**
   * Confirmation mode for the tool
   */
  confirm?: string | undefined;
  /**
   * Prompt for the confirmation
   */
  confirmPrompt?: string | undefined;
  /**
   * The creation date of the tool.
   */
  createdAt: Date;
  /**
   * The ID of the deployments_external_mcps record
   */
  deploymentExternalMcpId: string;
  /**
   * The ID of the deployment
   */
  deploymentId: string;
  /**
   * Description of the tool
   */
  description: string;
  /**
   * The ID of the tool
   */
  id: string;
  /**
   * The name of the tool
   */
  name: string;
  /**
   * The OAuth authorization endpoint URL
   */
  oauthAuthorizationEndpoint?: string | undefined;
  /**
   * The OAuth dynamic client registration endpoint URL
   */
  oauthRegistrationEndpoint?: string | undefined;
  /**
   * The OAuth scopes supported by the server
   */
  oauthScopesSupported?: Array<string> | undefined;
  /**
   * The OAuth token endpoint URL
   */
  oauthTokenEndpoint?: string | undefined;
  /**
   * OAuth version: '2.1' (MCP OAuth), '2.0' (legacy), or 'none'
   */
  oauthVersion: string;
  /**
   * The ID of the project
   */
  projectId: string;
  /**
   * The ID of the MCP registry
   */
  registryId: string;
  /**
   * The name of the external MCP server (e.g., exa)
   */
  registryServerName: string;
  /**
   * The specifier of the external MCP server (e.g., 'io.modelcontextprotocol.anonymous/exa')
   */
  registrySpecifier: string;
  /**
   * The URL to connect to the MCP server
   */
  remoteUrl: string;
  /**
   * Whether the external MCP server requires OAuth authentication
   */
  requiresOauth: boolean;
  /**
   * JSON schema for the request
   */
  schema: string;
  /**
   * Version of the schema
   */
  schemaVersion?: string | undefined;
  /**
   * The slug used for tool prefixing (e.g., github)
   */
  slug: string;
  /**
   * Summarizer for the tool
   */
  summarizer?: string | undefined;
  /**
   * The URN of this tool
   */
  toolUrn: string;
  /**
   * The transport type used to connect to the MCP server
   */
  transportType: TransportType;
  /**
   * Whether or not the tool is a proxy tool
   */
  type?: string | undefined;
  /**
   * The last update date of the tool.
   */
  updatedAt: Date;
  variation?: ToolVariation | undefined;
};

/** @internal */
export const TransportType$inboundSchema: z.ZodMiniEnum<typeof TransportType> =
  z.enum(TransportType);

/** @internal */
export const ExternalMCPToolDefinition$inboundSchema: z.ZodMiniType<
  ExternalMCPToolDefinition,
  unknown
> = z.pipe(
  z.object({
    annotations: z.optional(ToolAnnotations$inboundSchema),
    canonical: z.optional(CanonicalToolAttributes$inboundSchema),
    canonical_name: z.string(),
    confirm: z.optional(z.string()),
    confirm_prompt: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    deployment_external_mcp_id: z.string(),
    deployment_id: z.string(),
    description: z.string(),
    id: z.string(),
    name: z.string(),
    oauth_authorization_endpoint: z.optional(z.string()),
    oauth_registration_endpoint: z.optional(z.string()),
    oauth_scopes_supported: z.optional(z.array(z.string())),
    oauth_token_endpoint: z.optional(z.string()),
    oauth_version: z.string(),
    project_id: z.string(),
    registry_id: z.string(),
    registry_server_name: z.string(),
    registry_specifier: z.string(),
    remote_url: z.string(),
    requires_oauth: z.boolean(),
    schema: z.string(),
    schema_version: z.optional(z.string()),
    slug: z.string(),
    summarizer: z.optional(z.string()),
    tool_urn: z.string(),
    transport_type: TransportType$inboundSchema,
    type: z.optional(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    variation: z.optional(ToolVariation$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "canonical_name": "canonicalName",
      "confirm_prompt": "confirmPrompt",
      "created_at": "createdAt",
      "deployment_external_mcp_id": "deploymentExternalMcpId",
      "deployment_id": "deploymentId",
      "oauth_authorization_endpoint": "oauthAuthorizationEndpoint",
      "oauth_registration_endpoint": "oauthRegistrationEndpoint",
      "oauth_scopes_supported": "oauthScopesSupported",
      "oauth_token_endpoint": "oauthTokenEndpoint",
      "oauth_version": "oauthVersion",
      "project_id": "projectId",
      "registry_id": "registryId",
      "registry_server_name": "registryServerName",
      "registry_specifier": "registrySpecifier",
      "remote_url": "remoteUrl",
      "requires_oauth": "requiresOauth",
      "schema_version": "schemaVersion",
      "tool_urn": "toolUrn",
      "transport_type": "transportType",
      "updated_at": "updatedAt",
    });
  }),
);

export function externalMCPToolDefinitionFromJSON(
  jsonString: string,
): SafeParseResult<ExternalMCPToolDefinition, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ExternalMCPToolDefinition$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ExternalMCPToolDefinition' from JSON`,
  );
}
